import { z } from 'zod'

/**
 * Types and schemas for survey-related Firebase Functions
 */

// Modify Survey schemas
export const ModifySurveyRequestSchema = z.object({
  companyId: z.string().min(1),
  siteId: z.string().min(1),
  unitId: z.string().min(1),
  surveyId: z.string().min(1),
  modifiedSurveyData: z.record(z.string(), z.unknown()),
  preview: z.boolean(),
  /** When false, only save survey answers without regenerating MCP. Defaults to true when omitted. */
  updateProgram: z.boolean().optional(),
  /** Expected task diff counts from preview — used to detect stale previews */
  expectedTasksAdded: z.number().optional(),
  expectedTasksRemoved: z.number().optional(),
})

export type ModifySurveyRequest = z.infer<typeof ModifySurveyRequestSchema>

export interface ModifySurveyTaskChange {
  task: string
  codeRef: string
  itemId: string
  /** True if this removed task had completed instances that will be lost */
  hadCompletedInstances?: boolean
}

export interface ModifySurveyResponse {
  tasksAdded: ModifySurveyTaskChange[]
  tasksRemoved: ModifySurveyTaskChange[]
  tasksUnchanged: number
  /** False when the unit has no existing maintenance program — apply with updateProgram=true will fail in this state. */
  hasCurrentProgram: boolean
  applied: boolean
}

// Remove Last Survey schemas
export const RemoveLastSurveyRequestSchema = z.object({
  companyId: z.string().min(1),
  siteId: z.string().min(1),
  unitId: z.string().min(1),
  preview: z.boolean(),
  /** Required on apply (preview === false). Returned by the preview call so the
   * server can detect a newer survey landing between preview and apply. */
  expectedSurveyId: z.string().min(1).optional(),
  /** Required on apply. The set of completed program-item document ids
   * (`maintenanceProgramItemId`) the admin saw in the preview. The server
   * compares this against the current set and rejects with `aborted` if any
   * id was added or removed (catches "one item uncompleted, another
   * completed" cases that a count alone would miss). */
  expectedCompletedProgramItemIds: z.array(z.string().min(1)).optional(),
})

export type RemoveLastSurveyRequest = z.infer<
  typeof RemoveLastSurveyRequestSchema
>

export interface RemoveLastSurveyCompletedItem {
  /** Unique program-item document id; use this for React keys and the
   * apply-time staleness fingerprint. */
  maintenanceProgramItemId: string
  /** Maintenance item definition reference — not unique within a program
   * (e.g. quarterly tasks repeat). */
  itemId: string
  codeRef: string | null
  task: string
  /** Display name or email — already human-readable on the maintenance item */
  completedBy: string
  /** ISO timestamp */
  completedAt: string
}

export interface RemoveLastSurveyPreviousProgram {
  programId: string
  /** 1-indexed (1..12) */
  startDateMonth: number
  startDateYear: number
  /** True when the program is still within 12 months of its start date — will be restored on apply. */
  withinTwelveMonths: boolean
}

export interface RemoveLastSurveyResponse {
  surveyExists: boolean
  survey?: {
    surveyId: string
    /** ISO timestamp */
    submittedAt: string
    submittedBy: string
  }
  program?: {
    programId: string
    /** 1-indexed (1..12) */
    startDateMonth: number
    startDateYear: number
  } | null
  completedItems: RemoveLastSurveyCompletedItem[]
  previousProgram?: RemoveLastSurveyPreviousProgram | null
  /**
   * True when the unit has another isCurrent=true program besides the one
   * tied to the survey being removed. Typically the auto-renewed clone:
   * the survey's original program is non-current after auto-renewal, and
   * the cloned successor still covers the unit. The dialog uses this to
   * suppress misleading "Unit will be flagged for re-survey" warnings.
   */
  hasSurvivingCurrentProgram?: boolean
  applied: boolean
  /** Apply only — number of program items deleted */
  deletedItemCount?: number
  /** Apply only — true when a previous program was flipped to isCurrent */
  restoredPreviousProgram?: boolean
  message?: string
}
